import { Sortable } from 'gf-types';
import { KeyPath, TemplateSelectionRectangle } from '../templateeditor/templateeditor-types';
import { FieldTypes, SubscriptionCodes } from './constants';

export type SortProperty = 'sortOrder' | 'sortNumber';

export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

export const getFriendlyNameForType = (type) => {
  switch (type) {
    case FieldTypes.AutoNumber:
      return 'Auto Number';
    case FieldTypes.Barcode:
      return 'Barcode';
    case FieldTypes.CheckBox:
      return 'Checkbox';
    case FieldTypes.CheckBoxGroup:
      return 'Checkbox Group';
    case FieldTypes.Database:
      return 'Database';
    case FieldTypes.Date:
      return 'Date';
    case FieldTypes.DateTime:
      return 'Date Time';
    case FieldTypes.DropDown:
      return 'Drop Down';
    case FieldTypes.GPS:
      return 'Location';
    case FieldTypes.Image:
      return 'Image';
    case FieldTypes.Number:
      return 'Number';
    case FieldTypes.Signature:
      return 'Signature';
    case FieldTypes.eSignature:
      return 'eSign Signature';
    case FieldTypes.eSignatureInitials:
      return 'eSign Initials';
    case FieldTypes.Sketch:
      return 'Sketch';
    case FieldTypes.Table:
      return 'Table';
    case FieldTypes.Time:
      return 'Time';
    case FieldTypes.FileAttachment:
      return 'File Attachment';
    case FieldTypes.Audio:
      return 'Audio';
    case FieldTypes.TextBox:
    case FieldTypes.TextArea:
    default:
      return 'Text';
  }
};

// These values must match AccountManagement.Editions table
export function getCurrentEditionPriority(editionCode: string): number {
  switch (editionCode) {
    case SubscriptionCodes.Basic:
      return 0;
    case SubscriptionCodes.Trial:
    case SubscriptionCodes.Teacher:
    case SubscriptionCodes.Team:
      return 1;
    case SubscriptionCodes.Advanced:
      return 3;
    case SubscriptionCodes.Enterprise:
      return 5;
    default:
      return 0;
  }
}

export const calculateWidestPage = (pages) => {
  let maxPageWidth = 0;
  for (const page of pages) {
    const pageWidth = page.get ? page.get('width') : page.width;
    if (pageWidth > maxPageWidth) {
      maxPageWidth = pageWidth;
    }
  }
  return maxPageWidth;
};

export const intersectRect = <T extends TemplateSelectionRectangle>(r1: T, r2: T): boolean => {
  return !(r2.left > r1.right || r2.right < r1.left || r2.top > r1.bottom || r2.bottom < r1.top);
};

export const getFormattedColor = (color: string, defaultColor: string) => {
  let formattedColor = color;
  if (!formattedColor) {
    formattedColor = defaultColor;
  }
  if (formattedColor[0] !== '#') {
    formattedColor = `#${formattedColor}`;
  }
  return formattedColor;
};

export const getShortColor = (color: string) => {
  let backgroundColor = color;
  if (backgroundColor.length === 5) {
    backgroundColor = backgroundColor.substring(0, 4);
  } else if (backgroundColor.length === 9) {
    backgroundColor = backgroundColor.substring(0, 7);
  }
  return backgroundColor;
};

export const getRgba = (hex, defaultOpacity?): Rgba => {
  const rgba: Partial<Rgba> = {};

  if (hex.length === 4 || hex.length === 5) {
    rgba.r = parseInt(`${hex.substring(1)}${hex.substring(1)}`, 16);
    rgba.g = parseInt(`${hex.substring(2)}${hex.substring(2)}`, 16);
    rgba.b = parseInt(`${hex.substring(3)}${hex.substring(3)}`, 16);

    if (hex.length === 5) {
      rgba.a = parseInt(`${hex.substring(4)}${hex.substring(4)}`, 16) / 255;
    } else {
      rgba.a = defaultOpacity;
    }
  } else if (hex.length === 7 || hex.length === 9) {
    rgba.r = parseInt(hex.substring(1, 3), 16);
    rgba.g = parseInt(hex.substring(3, 5), 16);
    rgba.b = parseInt(hex.substring(5, 7), 16);

    if (hex.length === 9) {
      rgba.a = parseInt(hex.substring(7), 16) / 255;
    } else {
      rgba.a = defaultOpacity;
    }
  }

  return rgba as Rgba;
};

export const getHex = (value: number) => {
  // eslint-disable-next-line no-bitwise
  let hex = (value & 0xff).toString(16);
  if (hex.length === 1) {
    hex = `0${hex}`;
  }
  return hex;
};

export const getAlphaHex = (alpha: number) => {
  // eslint-disable-next-line no-bitwise
  return `0${((alpha * 255) & 0xff).toString(16)}`.slice(-2);
};

export const getColorOpacity = (color, defaultOpacity) => {
  return getRgba(color, defaultOpacity).a;
};

export const getRgbaColor = (color, defaultColor?, defaultOpacity?) => {
  const fullBackgroundColor = getFormattedColor(color, defaultColor);
  return getRgba(fullBackgroundColor, defaultOpacity);
};

export const getRgbaColorString = (rgba: Rgba) => {
  return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
};

export const getHexColorString = (rgba: Rgba) => {
  return `#${getHex(rgba.r)}${getHex(rgba.g)}${getHex(rgba.b)}`;
};

export const getOpacityFromRgba = (rgba: Rgba) => {
  return rgba.a;
};

export const replaceInvalidCharacters = (string: string) => {
  return string.replace(/\r?\n|\s|\xA0/g, ' ');
};

export function getIn<T>(obj: unknown, keyPath: KeyPath): T {
  let currentObj = obj;
  for (let i = 0; i < keyPath.length; i++) {
    if (i + 1 === keyPath.length) {
      return currentObj[keyPath[i]];
    } else {
      currentObj = currentObj[keyPath[i]];
    }
  }
}

/** This function mutates the given object */
export function setIn<T, TValue>(obj: T, keyPath: KeyPath, value: TValue): void {
  let currentObj = obj;
  for (let i = 0; i < keyPath.length; i++) {
    if (i + 1 === keyPath.length) {
      currentObj[keyPath[i]] = value;
    } else {
      currentObj = currentObj[keyPath[i]];
    }
  }
}

/** This function mutates the given object */
export function deleteIn<T>(obj: T, keyPath: KeyPath): void {
  let currentObj = obj;
  for (let i = 0; i < keyPath.length; i++) {
    if (i + 1 === keyPath.length) {
      if (Array.isArray(currentObj)) {
        // delete the element out of the array instead of making it
        // undefined
        currentObj.splice(keyPath[i] as number, 1);
      } else {
        delete currentObj[keyPath[i]];
      }
    } else {
      currentObj = currentObj[keyPath[i]];
    }
  }
}

/**
 * Inserts an item into the items collection and returns a reference to the same array
 *
 * Alias `Array.splice`.
 *
 * @param items The items array.
 * @param index The index to insert at.
 * @param item The item to insert
 */
export function insertAt<T>(items: T[], index: number, item: T): T[] {
  items.splice(index, 0, item);

  return items;
}

export function deleteAt<T>(items: T[], index: number) {
  return items.filter((_, idx) => idx !== index);
}

/**
 * Sorts the given Sortable array and returns a new sorted array.
 *
 * @param array The collection to sort.
 * @param sortProperty The property to sort on.
 * @param ascending True, for ascending, false for descending.
 */
export function sortIn<T extends Sortable>(array: T[], sortProperty: SortProperty = 'sortOrder', ascending = true): T[] {
  if (!array) {
    return array;
  }

  const sorted = array
    .slice()
    .filter((item) => !!item)
    .sort((a, b) => (ascending ? a[sortProperty] - b[sortProperty] : b[sortProperty] - a[sortProperty]));

  return sorted;
}

/**
 * Validates the url to make sure that it starts with `https://`.
 *
 * Falsy values are also valid.
 *
 * @param url The url to validate.
 */
export const validateUrl = (url: string, allowCalculations = true): boolean => {
  const validUrlRegEx = /(^https:\/\/)/is;

  if (!url) {
    return true;
  }

  if (allowCalculations) {
    return /(^=)/.test(url) || validUrlRegEx.test(url);
  }

  return validUrlRegEx.test(url);
};

export const getUniqueEventId = (eventName: string) => {
  const date = new Date();
  return `${eventName}.${date.toISOString()}.${date.getMilliseconds()}`;
};
